// domain/services/codeString.js

/**
 * Reading and writing the CODE STRING — the characters a funder bills under, as distinct from the
 * code record that carries them.
 *
 * ONE NORMALIZATION, FIVE CALLERS. `findByCodeOn` matches on it, the picker's search matches on it,
 * the mint form validates it, the document id embeds it, and the migration classifier's
 * `findCatalogCode` matches on it from the other side of a process boundary. Four of those live
 * here and now share this; the fifth cannot, and that limit is stated below rather than hidden.
 *
 * NOT A VALUE OBJECT, and the decision is worth recording because the shape invites one. This
 * context extracted `EffectivePeriod` when range ALGEBRA had spread across an entity and three
 * services — four places all knowing that two fields were one concept, each re-deriving the
 * intersection. Nothing like that is happening here: there is one transformation, no pairing
 * invariant, and no second field it must stay consistent with. A frozen single-field wrapper would
 * satisfy every mechanical test for a value object and fail the conceptual one — the positive
 * criterion is a descriptive aspect of the domain, not the absence of an id.
 *
 * The stronger argument against is that it could not span the boundary it would exist to protect.
 * `scripts/migrations/transforms/serviceCodeMigration.js` is CommonJS running under the admin SDK
 * and cannot import this module at all, so the pairing hazard survives whatever shape this takes.
 * A function single-sources the four callers that CAN share it and leaves the fifth honestly named.
 *
 * @see serviceCodeValidity.findByCodeOn — the by-string lookup that must agree with the classifier
 */

/**
 * Firestore rejects these outright in a document id, and a code string reaches one: a custom code's
 * id embeds it. `/` splits the path, a lone `.` or `..` names a relative segment, and `__x__` is
 * reserved. A code carrying one is refused at the form rather than sanitised — silently rewriting
 * the characters a funder bills under would produce a record that does not say what the org typed.
 *
 * `_` IS OURS RATHER THAN FIRESTORE'S, and it is the one here that buys a guarantee instead of
 * avoiding an error. The id joins clinic, code and start date with it, so a code carrying one makes
 * the three impossible to tell apart: clinic `x` with code `A_B` and clinic `x_A` with code `B`
 * derive the same id for a date. That is reachable rather than theoretical — `clinics/{id}` takes a
 * create from any signed-in user under any id (HR-4623), and a user can then work themselves up to
 * manager there — so whoever mints second is told they already hold a code they do not, and cannot
 * mint it at all. Refusing `_` here is what makes the id parse from the right: the start date is a
 * calendar date and carries none, the code now carries none, so everything before them is the
 * clinic. Constraining the parts rather than the id space is also the half we control.
 */
const FORBIDDEN_IN_DOCUMENT_ID = /[/_]|^\.\.?$|^__.*__$/;

/** Firestore's own ceiling is 1500 bytes for a whole id; a code string is a segment of one. */
const MAX_LENGTH = 64;

/**
 * The comparable form of a code string: trimmed, upper-cased, or null when there is nothing to read.
 *
 * CASE-INSENSITIVE BECAUSE HCPCS CODES CARRY A LETTER and a person typing `h0032` means `H0032`.
 * Upper rather than lower because that is how every published set writes them, so the normalized
 * form is also the form a claim shows.
 *
 * @param {*} code
 * @returns {string|null}
 */
export function toCodeString(code) {
  if (typeof code !== 'string') {
    return null;
  }
  const trimmed = code.trim().toUpperCase();

  return trimmed.length === 0 ? null : trimmed;
} // toCodeString()

/**
 * Do two code strings name the same code? Null never matches, including another null.
 *
 * Two absent strings are not a match: nothing is being compared, and returning true would make an
 * empty search field match every code in the catalog.
 *
 * @param {*} left
 * @param {*} right
 * @returns {boolean}
 */
export function isSameCodeString(left, right) {
  const a = toCodeString(left);
  const b = toCodeString(right);

  return a !== null && a === b;
} // isSameCodeString()

/**
 * Why a code string cannot be stored.
 *
 * A NAMED VOCABULARY rather than bare literals, because these cross a module boundary: the mint use
 * case maps them into its own error vocabulary, and a typo in that map is a silent miss — the lookup
 * returns undefined and the caller falls through to a different failure. Same reason `COVERAGE_ERRORS`
 * exists next door. Values are camelCase, matching every other token vocabulary in this context.
 *
 * @domainSupport The rejection vocabulary for a code string that cannot be stored.
 * @typedef {'empty' | 'tooLong' | 'unstorableCharacters'} CodeStringRejection
 */
export const CODE_STRING_REJECTION = {
  EMPTY: 'empty',
  TOO_LONG: 'tooLong',
  UNSTORABLE_CHARACTERS: 'unstorableCharacters',
};

/**
 * Why this string may not be minted as a code, or null when it may.
 *
 * RETURNS A REASON RATHER THAN A BOOLEAN, because the caller is a form field and every one of these
 * needs its own wording. A boolean would collapse "you typed nothing" and "that character cannot be
 * stored" into one message that is wrong for both.
 *
 * THE LENGTH AND CHARACTER RULES ARE STORAGE'S, NOT THE DOMAIN'S. No published set has a rule
 * against a slash; Firestore does, and a custom code's document id embeds its string, so the
 * constraint is real even though it comes from underneath. Refused rather than escaped: an id
 * scheme that encoded the character would make two visibly different codes collide on one id, which
 * is the failure the scheme exists to prevent.
 *
 * @param {*} code
 * @returns {CodeStringRejection|null}
 */
export function codeStringRejection(code) {
  const normalized = toCodeString(code);

  if (normalized === null) {
    return CODE_STRING_REJECTION.EMPTY;
  }
  if (normalized.length > MAX_LENGTH) {
    return CODE_STRING_REJECTION.TOO_LONG;
  }
  return FORBIDDEN_IN_DOCUMENT_ID.test(normalized)
    ? CODE_STRING_REJECTION.UNSTORABLE_CHARACTERS
    : null;
} // codeStringRejection()
